import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import { validatePending, createCheckoutSession } from '../../Authentication Services/AuthService';

const plans = [
  { tier: 1 },
  { tier: 2 },
  { tier: 3 },
  { tier: 4 }
];

function getQueryParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

const ChoosePlan = () => {
  const [userId] = useState(() => getQueryParam('userId') || '');
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [isContinuing, setIsContinuing] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const { t, i18n } = useTranslation('choosePlan');
  const supported = (i18n.options?.supportedLngs || ['en', 'es']).filter(l => l !== 'cimode');
  const initialLang = supported.includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';
  const [lang, setLang] = useState(initialLang);

  const selectedPlan = useMemo(
    () => plans.find(p => p.tier === selectedTier) || null,
    [selectedTier]
  );

  useEffect(() => {
    const saved = localStorage.getItem('ui.lang');
    const nextLang = supported.includes(saved) ? saved : initialLang;

    if (nextLang !== i18n.resolvedLanguage) {
      i18n.changeLanguage(nextLang);
    }

    setLang(nextLang);
  }, [i18n, initialLang, supported]);

  useEffect(() => {
    if (!userId) {
      setStatus('error');
      setErrorMsg(t('error.missingLink'));
      return;
    }

    setStatus('loading');

    validatePending(userId)
      .then(res => {
        const data = res.data;

        if (!data?.isValid) {
          setStatus('error');
          setErrorMsg(data?.message || t('error.notEligible'));
          return;
        }

        setShopName(data?.barberShopName || '');
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg(t('error.unableToValidate'));
      });
  }, [userId, t]);

  useEffect(() => {
    const canceled = getQueryParam('canceled');

    if (canceled === '1') {
      setIsCancelled(true);

      const url = new URL(window.location);
      url.searchParams.delete('canceled');
      window.history.replaceState({}, '', url);
    }
  }, []);

  const onContinue = async () => {
    if (!selectedTier) return;

    setIsContinuing(true);

    try {
      const tierNumber =
        selectedTier === 1 ? 1 :
        selectedTier === 2 ? 2 :
        selectedTier === 3 ? 3 :
        selectedTier === 4 ? 4 :
        null;

      if (!tierNumber) {
        alert(t('alerts.invalidTier'));
        return;
      }

      const res = await createCheckoutSession(userId, tierNumber);
      const url = res?.data?.url;

      if (!url) {
        alert(t('alerts.unableToCreateCheckout'));
        return;
      }

      window.location.href = url;
    } catch (e) {
      alert(t('alerts.unableToContinuePayment'));
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <div className="login-background">
      <div className="login-container choose-plan-container">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <label htmlFor="lang-switch" className="visually-hidden">
            {t('lang.ariaLabel')}
          </label>

          <div className="centered-select" />

          <select
            id="lang-switch"
            value={lang}
            onChange={(e) => {
              const newLang = e.target.value;
              setLang(newLang);
              i18n.changeLanguage(newLang);
              localStorage.setItem('ui.lang', newLang);
            }}
            aria-label={t('lang.ariaLabel')}
            className="lang-select"
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #ddd',
              background: '#fff'
            }}
          >
            {supported.map(code => (
              <option key={code} value={code}>
                {t(`lang.names.${code}`, code.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        {isCancelled && (
          <div className="choose-plan-cancel-banner">
            {t('banner.cancelled')}
          </div>
        )}

        <div className="choose-plan-header">
          <h2 className="login-title">{t('header.title')}</h2>
          <p className="login-subtitle">
            {shopName ? (
              t('header.subtitleWithShop', { shopName })
            ) : (
              t('header.subtitleDefault')
            )}
          </p>
        </div>

        {status === 'loading' && (
          <div className="choose-plan-state">
            <div className="choose-plan-spinner" aria-hidden="true" />
            <div className="choose-plan-state-text">
              {t('status.loading')}
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="choose-plan-error">
            <div className="choose-plan-error-title">{t('error.title')}</div>
            <div className="choose-plan-error-text">{errorMsg}</div>

            <div className="choose-plan-actions-row">
              <a className="choose-plan-link" href="/register">
                {t('actions.backToRegistration')}
              </a>
              <a className="choose-plan-link" href="/login">
                {t('actions.goToLogin')}
              </a>
            </div>
          </div>
        )}

        {status === 'ready' && (
          <>
            <div className="choose-plan-grid" role="list">
              {plans.map((p) => {
                const isSelected = p.tier === selectedTier;

                return (
                  <button
                    key={p.tier}
                    type="button"
                    role="listitem"
                    className={`choose-plan-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setSelectedTier(p.tier)}
                  >
                    <div className="choose-plan-card-top">
                      <div className="choose-plan-card-title">
                        {t(`plans.${p.tier}.title`)}
                        <span className="choose-plan-tag">
                          {t(`plans.${p.tier}.tag`)}
                        </span>
                      </div>

                      <div className="choose-plan-price">
                        <span className="choose-plan-price-main">
                          {t(`plans.${p.tier}.price`)}
                        </span>
                        <span className="choose-plan-price-sub">
                          {t(`plans.${p.tier}.period`)}
                        </span>
                      </div>

                      <div className="choose-plan-trial">
                        {t(`plans.${p.tier}.trial`)}
                      </div>
                    </div>

                    <div className="choose-plan-divider" />

                    <ul className="choose-plan-features">
                      <li className="choose-plan-feature">
                        <span className="choose-plan-check" aria-hidden="true">✓</span>
                        <span>{t(`plans.${p.tier}.limits.staff`)}</span>
                      </li>
                      <li className="choose-plan-feature">
                        <span className="choose-plan-check" aria-hidden="true">✓</span>
                        <span>{t(`plans.${p.tier}.limits.services`)}</span>
                      </li>
                      {p.tier >= 2 ?
                      <li className="choose-plan-feature">
                        <span className="choose-plan-check" aria-hidden="true">✓</span>
                        <span>Accept online payments using stripe connect</span>
                      </li>
                    :
                    null}
                    {p.tier >= 2 ?
                      <li className="choose-plan-feature">
                        <span className="choose-plan-check" aria-hidden="true">✓</span>
                        <span>Mini site to build online presence</span>
                      </li>
                    :
                    null}
                    </ul>

                    <div className="choose-plan-note">
                      {t(`plans.${p.tier}.note`)}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="choose-plan-cta">
              <button
                className="signin-btn"
                onClick={onContinue}
                disabled={!selectedTier || isContinuing}
              >
                {isContinuing ? <span className="loader" /> : t('actions.continueToPayment')}
              </button>

              <div className="choose-plan-summary">
                {selectedPlan ? (
                  t('summary.selected', {
                    plan: t(`plans.${selectedPlan.tier}.title`),
                    price: t(`plans.${selectedPlan.tier}.price`),
                    period: t(`plans.${selectedPlan.tier}.period`)
                  })
                ) : (
                  t('summary.empty')
                )}
              </div>

              <div className="choose-plan-footer-links">
                <a className="choose-plan-link" href="/login">
                  {t('actions.alreadySubscribed')}
                </a>
              </div>
            </div>

            <div className="choose-plan-smallprint">
              {t('smallprint.stripe')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChoosePlan;